import { bopApi } from '@/lib/api/client';
import { HTTP_INTERNAL_SERVER_ERROR, isHttpStatus, isNotFoundError } from '@/lib/api/error';
import { utcEndOfDayIso, utcStartOfDayIso } from '@/utils/date';

const TRANSACTIONS_PATH = 'v1/funds/transactions';
const TRANSFERS_PATH = 'v1/transfers';
const UPDATE_STATUS_PATH = 'v1/user/funds/transaction/update';
const REFUND_PATH = 'v1/funds/refund';
const CREATE_MANUAL_PATH = 'v1/funds/transactions/create';

/** Legacy panel always requests 40 rows per page (`max_page_size`). */
export const TRANSACTIONS_PAGE_SIZE = 40;

export const TRANSFER_DIRECTIONS = ['incoming', 'outgoing'] as const;
export type TransferDirection = (typeof TRANSFER_DIRECTIONS)[number];

/** Display mapping only — the filter/wire values stay incoming/outgoing (legacy parity). */
export const DIRECTION_LABELS: Record<TransferDirection, string> = {
  incoming: 'deposit',
  outgoing: 'withdrawal',
};

/** Filterable status values (filter chip options). */
export const TRANSFER_STATUSES = ['INITIATED', 'CONFIRMED', 'CANCELLED', 'CREDITED', 'REJECTED', 'PENDING', 'DONE', 'FAILED', 'UNDER_REVIEW', 'CARD_PENDING'] as const;
export type TransferStatus = (typeof TRANSFER_STATUSES)[number];

/** Editable target statuses (detail-dialog select) — deliberately NO INITIATED, NO CREDITED. */
export const EDITABLE_TRANSFER_STATUSES = ['CONFIRMED', 'CANCELLED', 'REJECTED', 'PENDING', 'CARD_PENDING', 'DONE', 'FAILED', 'UNDER_REVIEW'] as const;
export type EditableTransferStatus = (typeof EDITABLE_TRANSFER_STATUSES)[number];

export const TRANSFER_PROVIDERS = ['BCB', 'BROKER', 'CHECKOUT', 'CLEARBANK', 'FIREBLOCKS', 'FRICK', 'LOCAL'] as const;
export type TransferProvider = (typeof TRANSFER_PROVIDERS)[number];

export const TRANSFER_METHOD_TYPES = [
  'ADJUSTMENT',
  'BLINC',
  'BLOCKCHAIN',
  'CARD',
  'FEE',
  'INSTANT_TRADE_ACCOUNT_TRANSFER',
  'INSTANT_TRADE_FEE',
  'INTERNAL',
  'KINEXYS',
  'SEPA_INSTANT',
  'SEPA',
  'SUBACCOUNT_TRANSFER',
  'SWIFT',
  'WIRE',
] as const;
export type TransferMethodType = (typeof TRANSFER_METHOD_TYPES)[number];

/**
 * Transaction record — raw snake_case from funds. The record may contain MORE
 * fields than listed here; the detail dialog renders every key it receives.
 */
export interface Transaction {
  transaction_id: string;
  account_holder_id: string;
  account_id: string;
  /** date-ish — epoch s/ms or ISO, see formatDate */
  user_requested_at: string | number;
  user_confirmed_at?: string | number;
  completed_at?: string | number;
  direction: TransferDirection;
  amount: string;
  net_amount?: string;
  fee_amount?: string;
  currency: string;
  transfer_status: TransferStatus;
  transfer_provider: TransferProvider;
  transfer_method_type: TransferMethodType;
  source_account?: string;
  destination_account?: string;
  payout_account_label?: string;
  iban?: string;
  bic?: string;
  broker_batch_id?: string;
  [additionalData: string]: unknown;
}

export const TRANSACTION_FILTER_FIELDS = [
  'account_holder_id',
  'account_id',
  'direction',
  'currency',
  'transfer_status',
  'transfer_provider',
  'transfer_method_type',
  'transaction_id',
  'broker_batch_id',
] as const;
export type TransactionFilterField = (typeof TRANSACTION_FILTER_FIELDS)[number];

/** Selected values per UI filter field; multi-text filters store a single-element array. */
export type TransactionFilters = Partial<Record<TransactionFilterField, string[]>>;

/**
 * UI field → funds wire key. Must match funds' TransactionFilterQueryParamsBOP
 * exactly: currency→currencies, transfer_status→status,
 * transfer_method_type→transfer_method; everything else maps to itself.
 */
const FILTER_WIRE_KEYS = {
  account_holder_id: 'account_holder_id',
  account_id: 'account_id',
  direction: 'direction',
  currency: 'currencies',
  transfer_status: 'status',
  transfer_provider: 'transfer_provider',
  transfer_method_type: 'transfer_method',
  transaction_id: 'transaction_id',
  broker_batch_id: 'broker_batch_id',
} as const satisfies Record<TransactionFilterField, string>;

/** Request body for POST /v1/funds/transactions. All filter values are comma-joined strings. */
export interface TransactionsSearchRequest {
  max_page_size: number;
  cursor?: string;
  account_holder_id?: string;
  account_id?: string;
  direction?: string;
  currencies?: string;
  status?: string;
  transfer_provider?: string;
  transfer_method?: string;
  transaction_id?: string;
  broker_batch_id?: string;
  from?: string;
  to?: string;
}

export interface TransactionsSearchResponse {
  transactions: Transaction[];
  /** Present only when another page exists (and the page was non-empty). */
  cursor?: string;
}

export interface TransactionsSearchParams {
  filters: TransactionFilters;
  /** YYYY-MM-DD calendar dates from the pickers */
  fromDate?: string;
  toDate?: string;
  cursor?: string;
  /** User-page embed: forces account_holder_id regardless of filters */
  accountHolderId?: string;
}

export const buildTransactionsSearchRequest = ({ filters, fromDate, toDate, cursor, accountHolderId }: TransactionsSearchParams): TransactionsSearchRequest => {
  const filterEntries = Object.fromEntries(
    (Object.entries(filters) as Array<[TransactionFilterField, string[]]>)
      .filter(([, values]) => values.length > 0)
      .map(([field, values]) => [FILTER_WIRE_KEYS[field], values.join(',')]),
  );
  return {
    max_page_size: TRANSACTIONS_PAGE_SIZE,
    ...(cursor ? { cursor } : {}),
    ...filterEntries,
    ...(accountHolderId ? { account_holder_id: accountHolderId } : {}),
    // Deliberate fix (legacy bug): the Nuxt route rounded even absent dates, producing
    // `from: null, to: null` (Invalid Date → JSON null). Omit the keys entirely instead.
    // UTC day-rounding (moved from the Nuxt BFF funds/transactions.post.ts) — see utils/date.
    ...(fromDate ? { from: utcStartOfDayIso(fromDate) } : {}),
    ...(toDate ? { to: utcEndOfDayIso(toDate) } : {}),
  };
};

export const searchTransactions = (request: TransactionsSearchRequest, signal?: AbortSignal) =>
  bopApi.post(TRANSACTIONS_PATH, { json: request, signal }).json<TransactionsSearchResponse>();

/** Raw DynamoDB transfer record (FAST table). Single-letter attribute names are real. */
interface TransferDynamoRecord {
  p: string;
  s: string;
  r: string;
  /** AML report — pre-rendered text lines; may be absent */
  R?: string[];
  [additionalData: string]: unknown;
}

interface GetTransfersResponse {
  transfers: Record<string, TransferDynamoRecord>;
}

export type AmlReportLookup = Pick<Transaction, 'transaction_id' | 'account_holder_id' | 'direction'>;

/**
 * AML-report lookup for a single row. A 404 is the NORMAL case for rows without
 * an AML report — mapped to null, never surfaced as an error.
 */
export const fetchAmlReport = async ({ transaction_id, account_holder_id, direction }: AmlReportLookup): Promise<string[] | null> => {
  try {
    const response = await bopApi
      .post(TRANSFERS_PATH, { json: { transfers: [{ transactionId: transaction_id, pid: account_holder_id, direction }] } })
      .json<GetTransfersResponse>();
    return response.transfers[transaction_id]?.R ?? null;
  } catch (error) {
    if (isNotFoundError(error)) return null;
    throw error;
  }
};

export interface UpdateTransactionStatusRequest {
  pid: string;
  id: string;
  status: EditableTransferStatus;
  /** Audit justification (35–500 chars, enforced client-side only) */
  comment: string;
  approved_by?: string;
  cancelled_by?: string;
}

/**
 * CRITICAL: bop-api trusts approved_by/cancelled_by from the body; the
 * Nuxt BFF injected them from the server session, the React client injects the
 * operator's name here. No other keys may be sent — bop-api 400s on unknown properties.
 */
export const buildStatusUpdateRequest = (params: {
  pid: string;
  id: string;
  status: EditableTransferStatus;
  comment: string;
  operatorName: string;
}): UpdateTransactionStatusRequest => ({
  pid: params.pid,
  id: params.id,
  status: params.status,
  comment: params.comment,
  // approved_by is also sent for CARD_PENDING per the legacy rule, even though bop-api
  // currently drops it for that status — keeps behaviour identical once bop-api is hardened.
  ...(params.status === 'PENDING' || params.status === 'CARD_PENDING' ? { approved_by: params.operatorName } : {}),
  ...(params.status === 'CANCELLED' ? { cancelled_by: params.operatorName } : {}),
});

// 200 responses have an EMPTY body — never .json() this call.
export const updateTransactionStatus = (body: UpdateTransactionStatusRequest) => bopApi.post(UPDATE_STATUS_PATH, { json: body }).text();

export type RefundOutcome = 'ok' | 'probably-succeeded';

const REFUND_BUG_ERROR = 'Server error.';

/**
 * Refund an UNDER_REVIEW withdrawal.
 *
 * LEGACY BUG workaround: bop-api's refundTransaction.js success path never
 * returns ok() — a SUCCESSFUL refund falls through to HTTP 500 {"error":"Server error."}.
 * Until bop-api is fixed, that exact failure shape is mapped to 'probably-succeeded'
 * so callers warn-and-refetch instead of hard-failing.
 */
export const refundTransaction = async (body: { transactionId: string; comment: string }): Promise<RefundOutcome> => {
  try {
    await bopApi.post(REFUND_PATH, { json: body }).text();
    return 'ok';
  } catch (error) {
    if (isHttpStatus(error, HTTP_INTERNAL_SERVER_ERROR) && error.message === REFUND_BUG_ERROR) return 'probably-succeeded';
    throw error;
  }
};

export const MANUAL_TRANSACTION_ACTIONS = ['Credit', 'Debit'] as const;
export type ManualTransactionAction = (typeof MANUAL_TRANSACTION_ACTIONS)[number];

/** The API also accepts LOAN / LOAN_REFUND — deliberately not offered (legacy parity). */
export const MANUAL_TRANSACTION_REASONS = ['Credit', 'Debit', 'Other'] as const;
export type ManualTransactionReason = (typeof MANUAL_TRANSACTION_REASONS)[number];

/** Mirrors the API's amount rule: positive, one optional decimal point, no sign/commas. */
export const MANUAL_AMOUNT_PATTERN = /^\d+(\.\d+)?$/;
// Canonical home is utils/validation; re-exported because manual-transaction-dialog.tsx
// still imports it from here (dialog migrates in a later wave).
export { ACCOUNT_HOLDER_ID_LENGTH } from '@/utils/validation';

export interface ManualTransactionInput {
  accountHolderId: string;
  currencyCode: string;
  amount: string;
  action: ManualTransactionAction;
  reason: ManualTransactionReason;
  note: string;
  /** Audit justification */
  comment: string;
}

// Uppercasing moved from the Nuxt BFF (funds/manualTransaction.post.ts) — display
// values stay title-case in the dialog, the wire gets CREDIT/DEBIT/OTHER etc.
// bop-api sets approved_by/requested_by from the auth token; the client must NOT send them.
export const createManualTransaction = ({ accountHolderId, currencyCode, amount, action, reason, note, comment }: ManualTransactionInput) =>
  bopApi
    .post(CREATE_MANUAL_PATH, {
      json: {
        accountHolderId,
        currencyCode: currencyCode.toUpperCase(),
        amount,
        action: action.toUpperCase(),
        reason: reason.toUpperCase(),
        note,
        comment,
      },
    })
    .text();
